/* PDLYTE buy box helpers, loaded by snippets/pdx-tier-packs.liquid.
   1. Subscribe and save / Buy once switch at the top of the pack selector. Switching uses the
      existing purchase toggle in pdx.js, so pack selection, prices and add to bag stay there.
   2. Mobile: the sticky buy bar shows from the start of the page. Until the pack choices have been
      seen it reads "Choose your pack" and scrolls to them; after that it adds to bag as before. */
(() => {
  'use strict';
  if (window.pdxBuyboxLoaded) return;
  window.pdxBuyboxLoaded = true;

  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const mobile = matchMedia('(max-width: 749px)');
  const CHOOSE_LABEL = 'Choose your pack';

  const initSwitch = (packs, root) => {
    const choices = packs.querySelectorAll('[data-pdx-mode-choice]');
    if (!choices.length) return;
    const currentMode = () => packs.dataset.mode || 'subscription';
    const paint = () => {
      const mode = currentMode();
      choices.forEach((button) => {
        const on = button.dataset.pdxModeChoice === mode;
        button.classList.toggle('is-active', on);
        button.setAttribute('aria-pressed', String(on));
      });
    };
    choices.forEach((button) => {
      button.addEventListener('click', () => {
        if (currentMode() === button.dataset.pdxModeChoice) return;
        const toggle = root.querySelector('[data-pdx-purchase-toggle]');
        if (toggle) toggle.click();
        paint();
      });
    });
    if ('MutationObserver' in window) {
      new MutationObserver(paint).observe(packs, { attributes: true, attributeFilter: ['data-mode'] });
    }
    paint();
  };

  const initSticky = (packs, root) => {
    if (!('IntersectionObserver' in window)) return;
    const form = document.getElementById(packs.dataset.formId);
    const atc = form && form.querySelector('[data-pdx-atc]');
    const sticky = root._pdxSticky || root.querySelector('[data-pdx-sticky]') || document.querySelector('[data-pdx-sticky]');
    const button = sticky && sticky.querySelector('button');
    if (!atc || !button) return;
    const addLabel = button.textContent.trim();
    let seen = false;
    let atcInView = false;
    const paint = () => {
      const early = mobile.matches && !atcInView && !atc.disabled;
      sticky.classList.toggle('pdx-sticky--early', early);
      const label = mobile.matches && !seen ? CHOOSE_LABEL : addLabel;
      if (button.textContent !== label) button.textContent = label;
    };
    new IntersectionObserver(([entry]) => {
      atcInView = entry.isIntersecting;
      paint();
    }, { threshold: 0 }).observe(atc);
    new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      seen = true;
      paint();
    }, { threshold: 0.25 }).observe(packs);
    /* Capture phase on the button itself: runs before the add to bag handler in pdx.js. */
    button.addEventListener('click', (event) => {
      if (!mobile.matches || seen) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      seen = true;
      packs.scrollIntoView({ block: 'start', behavior: reduced() ? 'auto' : 'smooth' });
      paint();
    }, true);
    if (mobile.addEventListener) mobile.addEventListener('change', paint);
    paint();
  };

  /* 3. Free gift on the Joint Health Formula: a ticked gift card under the pack choices. When it is
     ticked, the gift variant is added in the same cart/add.js call that pdx.js makes for the pack
     (once per cart). The Sternify Gifts discount makes it free when the formula is in the cart. */
  const GIFT_ON = /\/products\/pdlyte-joint-formula(\/|\?|$)/;
  const GIFT_HANDLE = 'joint-care-spray';
  const GIFT_CSS = '.pdx-gift{position:relative;display:grid;grid-template-columns:22px 56px 1fr auto;align-items:center;gap:12px;margin:0 0 14px;padding:12px 14px;border:2px dashed var(--pdx-ink);background:var(--pdx-tint);cursor:pointer}' +
    '.pdx-gift__check{position:absolute;opacity:0;width:1px;height:1px}' +
    '.pdx-gift__box{width:22px;height:22px;border:2px solid var(--pdx-ink);background:var(--pdx-surface);display:grid;place-items:center}' +
    '.pdx-gift__check:checked+.pdx-gift__box{background:var(--pdx-ink)}' +
    '.pdx-gift__check:checked+.pdx-gift__box::after{content:"";width:6px;height:11px;border:solid var(--pdx-badge-ink,#D5F66A);border-width:0 2px 2px 0;transform:translateY(-1px) rotate(45deg)}' +
    '.pdx-gift__check:focus-visible+.pdx-gift__box{outline:2px solid var(--pdx-ink);outline-offset:2px}' +
    '.pdx-gift:has(.pdx-gift__check:not(:checked)){border-style:solid;border-color:var(--pdx-line);background:var(--pdx-surface)}' +
    '.pdx-gift__img img{display:block;width:56px;height:56px;object-fit:cover;background:#fff}' +
    '.pdx-gift__copy{display:flex;flex-direction:column;gap:2px;min-width:0}' +
    '.pdx-gift__copy strong{font:700 15px/1.25 var(--pdx-body)}' +
    '.pdx-gift__copy small{font:400 12px/1.35 var(--pdx-body);color:var(--pdx-muted)}' +
    '.pdx-gift__badge{align-self:flex-start;padding:2px 6px;background:var(--pdx-badge,#0E3A2A);color:var(--pdx-badge-ink,#D5F66A);font:700 10px/1.3 var(--pdx-body);letter-spacing:.06em;text-transform:uppercase}' +
    '.pdx-gift__price{font:700 17px/1 var(--pdx-title)}' +
    '@media (max-width:599px){.pdx-gift{grid-template-columns:22px 48px 1fr auto;gap:10px;padding:10px 12px}.pdx-gift__img img{width:48px;height:48px}.pdx-gift__copy strong{font-size:14px}}';
  let giftId = 0;
  const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  const initGiftCard = async (packs) => {
    if (!GIFT_ON.test(location.pathname) || packs.parentNode.querySelector('[data-pdx-gift]')) return;
    const gift = await fetch(root() + 'products/' + GIFT_HANDLE + '.js', { headers: { Accept: 'application/json' } }).then((r) => (r.ok ? r.json() : null)).catch(() => null);
    const variant = gift && gift.variants && gift.variants.find((v) => v.available);
    if (!variant) return;
    giftId = variant.id;
    if (!document.getElementById('pdx-gift-css')) {
      const style = document.createElement('style');
      style.id = 'pdx-gift-css';
      style.textContent = GIFT_CSS;
      document.head.appendChild(style);
    }
    const img = gift.featured_image ? gift.featured_image.replace(/(\.[a-z]+)(\?|$)/i, '_160x160_crop_center$1$2') : '';
    const card = document.createElement('label');
    card.className = 'pdx-gift';
    card.setAttribute('data-pdx-gift', '');
    card.innerHTML = '<input type="checkbox" class="pdx-gift__check" data-pdx-gift-check checked>' +
      '<span class="pdx-gift__box" aria-hidden="true"></span>' +
      (img ? '<span class="pdx-gift__img"><img src="' + img + '" alt="" width="56" height="56" loading="lazy"></span>' : '<span></span>') +
      '<span class="pdx-gift__copy"><span class="pdx-gift__badge">Free gift</span><strong>Joint Care Spray</strong><small>30 ml bag size spray, added to your order</small></span>' +
      '<span class="pdx-gift__price">FREE</span>';
    packs.insertAdjacentElement('afterend', card);
  };

  const initGiftFetch = () => {
    if (window.pdxGiftFetch || !window.fetch) return;
    window.pdxGiftFetch = true;
    const nativeFetch = window.fetch.bind(window);
    window.fetch = async (input, init) => {
      try {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        const check = document.querySelector('[data-pdx-gift-check]');
        if (giftId && check && check.checked && /\/cart\/add\.js(\?|$)/.test(url) && init && typeof init.body === 'string') {
          const body = JSON.parse(init.body);
          if (Array.isArray(body.items) && !body.items.some((item) => Number(item.id) === giftId)) {
            const cart = await nativeFetch(url.replace(/cart\/add\.js.*$/, 'cart.js'), { headers: { Accept: 'application/json' } }).then((r) => r.json()).catch(() => null);
            if (!(cart && cart.items && cart.items.some((item) => item.variant_id === giftId))) {
              body.items.push({ id: giftId, quantity: 1 });
              init = Object.assign({}, init, { body: JSON.stringify(body) });
            }
          }
        }
      } catch (e) { /* fall through to the original request */ }
      return nativeFetch(input, init);
    };
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-pdx-subscription-tiers]').forEach((packs) => {
      if (packs.dataset.pdxBuybox) return;
      const root = packs.closest('[data-pdx-product]');
      if (!root) return;
      packs.dataset.pdxBuybox = '1';
      initSwitch(packs, root);
      initSticky(packs, root);
      initGiftCard(packs);
    });
  };

  initGiftFetch();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
